import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePDF } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { Feature } from 'geojson';
import { datadogLogs } from '@datadog/browser-logs';

import useCropSeasonServiceApi from 'hooks/useCropSeasonServiceApi';
import {
  CROP_SEASON_FETCH_V2,
  CROP_SEASON_FETCH_V2_BY_ORG
} from 'constants/cropSeasonServiceV2Flags';
import usePDFTools from 'hooks/usePDFTools';
import { useStore } from 'components/Store';
import OperationPdf from 'screens/Applications&WorkOrders/Components/OperationPdf';
import {
  buildFeature,
  convertFeaturesToMapUri
} from 'screens/Applications&WorkOrders/Components/OperationPdf/helpers/mapHelpers';
import {
  getProductsExtraData,
  getPropertyExtraData,
  getEquipmentNames,
  getPeopleByIds,
  getCompaniesByIds,
  getTagsByIds,
  fetchOperationProducts
} from 'screens/Applications&WorkOrders/Components/OperationPdf/helpers/apiHelpers';
import { sumFieldAcres } from 'screens/Applications&WorkOrders/Components/OperationPdf/helpers/tableHelpers';
import {
  getManufacturerName,
  getProductName
} from 'screens/ProductList/helpers/filteredProducts';
import { OperationType } from 'components/RecordsListView/constants';
import { Applicator } from '../StepsLayout/Constants/formikData';
import { mapWorkerProtectionToTable } from '../Components/OperationPdf/helpers/tableConfigs/productsTableConfig';
import { getTargetPests } from '../Components/helpers';

export type ApplicationStrategy =
  | 'ByRatePerTank'
  | 'ByRatePerArea'
  | 'ByTotalProduct';

export interface OperationExport {
  name: string;
  properties: any[];
  products: any[];
  cropSeasonIds: string[];
  applicationStrategy?: ApplicationStrategy;
  [key: string]: any;
}

export enum ExportLanguageEnum {
  ENGLISH = 'english',
  LATAM_SPANISH = 'LATAMSpanish'
}

export interface UseOperationPdfProps {
  orientation?: OrientationOptions;
  operationType?: OperationType;
  onError?: () => void;
  /**
   * Checked just before the finished PDF is delivered; return false to discard.
   * Rendering can't be aborted, so this is how a caller cancels.
   */
  shouldDeliver?: () => boolean;
}

export type OrientationOptions = 'portrait' | 'landscape';

const URL_STABILIZE_TIMEOUT = 1000;

const useOperationPdf = ({
  orientation = 'portrait',
  operationType = OperationType.APPLICATION,
  onError,
  shouldDeliver
}: UseOperationPdfProps = {}) => {
  const {
    state: { organization }
  } = useStore();
  const {
    downloadPDFV2,
    printPDFV2,
    downloading,
    setDownloading
  } = usePDFTools();
  const cropSeasonFetchApi = useCropSeasonServiceApi(
    'cropseason',
    CROP_SEASON_FETCH_V2,
    CROP_SEASON_FETCH_V2_BY_ORG
  );
  const [applicationToPdf, setApplicationToPdf] = useState<any>(null);
  const [shouldPrint, setShouldPrint] = useState(false);
  const isWorkOrder = operationType === OperationType.WORK_ORDER;

  // In a ref so the effect reads the latest predicate without depending on it.
  const shouldDeliverRef = useRef(shouldDeliver);
  shouldDeliverRef.current = shouldDeliver;

  // Handed to updateInstance once there is an operation to render. usePDF
  // starts without a document: given one on mount it renders it straight
  // away, and with no operation yet react-pdf throws reading `props` of null.
  const pdfDocument = (
    <OperationPdf
      application={applicationToPdf}
      orientation={orientation}
      operationType={operationType}
    />
  );

  const [instance, updateInstance] = usePDF();

  const prevPdfUrl = useRef<string | undefined>(undefined);
  const urlStabilizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    if (applicationToPdf) {
      updateInstance(pdfDocument);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationToPdf]);

  useEffect(() => {
    const clearStabilizeTimeout = () => {
      if (urlStabilizeTimeoutRef.current) {
        clearTimeout(urlStabilizeTimeoutRef.current);
        urlStabilizeTimeoutRef.current = null;
      }
    };

    const handlePdfAction = (url: string) => {
      const deliver = shouldDeliverRef.current?.() ?? true;

      if (deliver) {
        if (shouldPrint) {
          printPDFV2(url);
        } else {
          downloadPDFV2(
            url,
            `${isWorkOrder ? 'Work Order' : 'Application'} - ${
              applicationToPdf.name
            }.pdf`
          );
        }
      }

      setApplicationToPdf(null);
      setShouldPrint(false);
      setDownloading(false);
    };

    const isPdfReady =
      !instance.loading &&
      instance.url &&
      prevPdfUrl.current !== instance.url &&
      applicationToPdf;

    clearStabilizeTimeout();

    if (instance.error && applicationToPdf) {
      datadogLogs.logger.error(
        'PDF render failed for operation',
        {
          operationType,
          operationName: applicationToPdf.name
        },
        new Error(instance.error)
      );
      onError?.();
      setApplicationToPdf(null);
      setShouldPrint(false);
      setDownloading(false);
      return clearStabilizeTimeout;
    }

    if (isPdfReady && instance.url) {
      const currentUrl = instance.url;
      prevPdfUrl.current = currentUrl;

      // Wait for URL to stabilize (no new URL for 1000ms)
      // This ensures we wait for all re-renders to complete
      urlStabilizeTimeoutRef.current = setTimeout(() => {
        // Double-check the URL hasn't changed
        if (prevPdfUrl.current === currentUrl && applicationToPdf) {
          handlePdfAction(instance.url ?? prevPdfUrl.current);
        }
        urlStabilizeTimeoutRef.current = null;
      }, URL_STABILIZE_TIMEOUT);
    }

    return clearStabilizeTimeout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationToPdf, instance, setDownloading, shouldPrint]);

  const exportPdfMain = async (
    application: OperationExport,
    language: ExportLanguageEnum = ExportLanguageEnum.ENGLISH
  ) => {
    setDownloading(true);

    if (!application) {
      setDownloading(false);
      return;
    }

    try {
      const operationProductsPromise = fetchOperationProducts(
        application.id,
        isWorkOrder,
        organization.id
      );

      const features: Feature[] = [];
      const mappedPropertiesPromise = application.properties.map(
        async (property: any) => {
          const {
            cropZone: cropZoneInfo,
            field,
            farm,
            crop,
            centroId
          } = await getPropertyExtraData(property.cropZoneId);

          const geometry = (centroId as any)?.geometry;

          if (cropZoneInfo?.geometry) {
            features.push(buildFeature(cropZoneInfo.geometry));
          }

          // Store geometry for field icon rendering
          const fieldGeometry = cropZoneInfo?.geometry;

          return {
            ...property,
            farmName: farm.name,
            fieldName: field.name,
            cropZoneName: cropZoneInfo.name,
            cropName: crop.name,
            cropId: cropZoneInfo.cropId,
            latlong: geometry?.coordinates,
            fieldGeometry
          };
        }
      );

      const visibleProducts = (application.products || []).filter(
        (product: any) => !product.isHidden
      );

      const fullOperationProducts = (await operationProductsPromise) || [];

      const mappedProductsPromise = visibleProducts.map(
        async (product: any) => {
          const {
            masterProduct,
            activeIngredients,
            reiphisData,
            workerProtection
          } = await getProductsExtraData(product.productId);
          return {
            ...product,
            name: getProductName(product),
            manufacturer: getManufacturerName(product),
            ...masterProduct,
            activeIngredients,
            reiphisData,
            workerProtection,
            targetPests: getTargetPests(product, fullOperationProducts)
          };
        }
      );

      // Fetch all crop seasons in application
      const cropSeasonPromises = application.cropSeasonIds.map(
        id =>
          (cropSeasonFetchApi as any).fetch(
            id,
            undefined,
            undefined,
            undefined,
            true // ignoreCache
          ).promise
      );

      // Fetch equipment names
      const equipmentNamesPromise = getEquipmentNames(
        application.equipmentIds || []
      );

      // Fetch people for applicators and creator
      const applicatorIds =
        application.applicators?.map((a: Applicator) => a.personId) || [];
      const creatorId = application.createdBy;
      const authorizerId = application.authorization?.authorizerId;
      const allPersonIds = [...applicatorIds];
      if (creatorId) {
        allPersonIds.push(creatorId);
      }
      if (authorizerId) {
        allPersonIds.push(authorizerId);
      }
      const peoplePromise = getPeopleByIds(allPersonIds);

      // Fetch companies for applicators and authorizer
      const applicatorCompanyIds =
        application.applicators?.map((a: any) => a.companyId) || [];
      const allCompanyIds = [...applicatorCompanyIds, authorizerId].filter(
        Boolean
      );
      const companiesPromise = getCompaniesByIds(allCompanyIds);

      // Fetch tags
      const tagsPromise = getTagsByIds(application.tagIds || []);

      const mappedProperties = await Promise.allSettled(
        mappedPropertiesPromise
      );
      const mappedProducts = await Promise.allSettled(mappedProductsPromise);
      const cropSeasons = await Promise.allSettled(cropSeasonPromises);
      const equipmentNames = await equipmentNamesPromise;
      const people = await peoplePromise;
      const companies = await companiesPromise;
      const tags = await tagsPromise;

      // Create maps for easy lookup
      const peopleMap = new Map(people.map((p: any) => [p.id, p]));
      const companyMap = new Map(companies.map((c: any) => [c.id, c]));

      // Enrich applicators with person and company info
      const applicators =
        application.applicators?.map((applicator: any) => {
          const person = peopleMap.get(applicator.personId);
          const company = companyMap.get(applicator.companyId);
          return {
            ...applicator,
            personName: person?.name,
            companyName: company?.name,
            licenseNumber: person?.licenseNumber,
            expirationDate: person?.expirationDateEpoch
              ? format(new Date(person.expirationDateEpoch), 'MMM dd, yyyy')
              : undefined
          };
        }) || [];

      // Get creator name
      const creatorPerson = creatorId ? peopleMap.get(creatorId) : null;
      const createdByName = creatorPerson?.name;

      // Get authorizer name
      let authorizer = null;
      if (authorizerId) {
        authorizer =
          peopleMap.get(authorizerId) ?? companyMap.get(authorizerId);
        if (!authorizer) {
          authorizer = null;
        }
      }

      const totalFieldsArea = sumFieldAcres(application.properties);
      const generatedDate = new Date();
      const year = format(generatedDate, 'yyyy');
      const fullDate = {
        english: format(generatedDate, 'MMM dd, yyyy hh:mma'),
        LATAMSpanish: format(generatedDate, 'MMM dd, yyyy hh:mma')
      };
      const mapImage = await convertFeaturesToMapUri(features, orientation);
      setApplicationToPdf({
        ...application,
        fields: mappedProperties
          .filter(request => request.status === 'fulfilled')
          .map(request => request.value),
        organizationName: organization.name,
        cropSeasons: cropSeasons
          .filter(request => request.status === 'fulfilled')
          .map(request => request.value.data.name),
        products: mappedProducts
          .filter(request => request.status === 'fulfilled')
          .map(request => request.value),
        _exportLanguage: language,
        workerProtection: mapWorkerProtectionToTable(
          mappedProducts
            .filter(request => request.status === 'fulfilled')
            .map(request => request.value)
        ),
        totalFieldsArea,
        year,
        fullDate,
        mapImage,
        // Details data
        applicators,
        equipmentIds: equipmentNames.join(', '),
        createdBy: createdByName,
        authorization: {
          ...application.authorization,
          authorizedBy: authorizer?.name
        },
        tags
      });
    } catch (error) {
      datadogLogs.logger.error(
        'PDF export failed for operation',
        {
          operationType,
          operationId: application.id,
          operationName: application.name
        },
        error as Error
      );
      onError?.();
      setDownloading(false);
    }
  };

  const createPDF = async (
    application: OperationExport,
    language: ExportLanguageEnum,
    toPrint: boolean
  ) => {
    setShouldPrint(toPrint);
    await exportPdfMain(application, language);
  };

  const exportPdfEnglish = async (application: OperationExport) => {
    await createPDF(application, ExportLanguageEnum.ENGLISH, false);
  };

  const exportPdfSpanish = async (application: OperationExport) => {
    await createPDF(application, ExportLanguageEnum.LATAM_SPANISH, false);
  };

  const printPdfEnglish = async (application: OperationExport) => {
    await createPDF(application, ExportLanguageEnum.ENGLISH, true);
  };

  const printPdfSpanish = async (application: OperationExport) => {
    await createPDF(application, ExportLanguageEnum.LATAM_SPANISH, true);
  };

  /**
   * Abandons an in-flight export. `shouldDeliver` isn't consulted until the
   * render finishes, so on its own it leaves the downloading flag set.
   */
  const cancelPdf = useCallback(() => {
    setApplicationToPdf(null);
    setShouldPrint(false);
    setDownloading(false);
  }, [setDownloading]);

  return {
    downloadingPDF: downloading,
    cancelPdf,
    exportPdfEnglish,
    exportPdfSpanish,
    printPdfEnglish,
    printPdfSpanish,
    createPDF
  };
};

export default useOperationPdf;
